import { isPayloadType, type QrGeneratorSeed } from "../domain/seed";

// Inlined from web/src/lib/site/origins.ts — ADR-026 §6 plans to delete
// the origins helper in Phase 6 and keep only the cookie-domain bit
// here. The literal `process.env.NEXT_PUBLIC_*` access is mandatory for
// Next.js to inline the value into the client bundle (see the lengthy
// comment in the original origins.ts).
const TRIM_TRAILING_SLASH = /\/+$/;

function normaliseOrigin(raw: string | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim().replace(TRIM_TRAILING_SLASH, "");
  return trimmed.length === 0 ? null : trimmed;
}

function cookieDomain(): string | null {
  const marketing = normaliseOrigin(process.env.NEXT_PUBLIC_MARKETING_ORIGIN);
  const app = normaliseOrigin(process.env.NEXT_PUBLIC_APP_ORIGIN);
  if (!marketing || !app || marketing === app) return null;
  try {
    return `.${new URL(marketing).hostname}`;
  } catch {
    return null;
  }
}

/**
 * Carries the marketing-side editor snapshot across the OIDC roundtrip.
 *
 * Why a cookie instead of localStorage: ADR-022 splits the product into
 * `qrody.com` (marketing) and `app.qrody.com` (app). localStorage is
 * origin-scoped, so a draft saved on the marketing host is invisible
 * after the post-login redirect lands on the app host. A cookie scoped
 * to `.qrody.com` is shared by both subdomains and survives the hop.
 *
 * The body is still a JSON envelope with TTL + schema version — the
 * cookie is only the transport, the freshness guard lives inside the
 * payload so we don't depend on `Max-Age` semantics being honoured
 * uniformly across browsers (Safari ITP, in particular, can shorten
 * cookie lifetimes).
 */

const COOKIE_NAME = "qrody-draft";
const SCHEMA_VERSION = 1;
const DEFAULT_TTL_MS = 60 * 60 * 1000;

interface DraftEnvelope {
  v: number;
  expiresAt: number;
  seed: QrGeneratorSeed;
}

interface SaveOptions {
  ttlMs?: number;
  now?: () => number;
}

interface LoadOptions {
  now?: () => number;
}

function readRawCookie(): string | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie;
  if (!raw) return null;
  const prefix = `${COOKIE_NAME}=`;
  for (const part of raw.split(";")) {
    const trimmed = part.trim();
    if (!trimmed.startsWith(prefix)) continue;
    const value = trimmed.slice(prefix.length);
    try {
      return decodeURIComponent(value);
    } catch {
      return null;
    }
  }
  return null;
}

function writeCookie(value: string, ttlMs: number): void {
  if (typeof document === "undefined") return;
  const ttlSeconds = Math.max(1, Math.floor(ttlMs / 1000));
  const segments = [
    `${COOKIE_NAME}=${encodeURIComponent(value)}`,
    "Path=/",
    `Max-Age=${ttlSeconds}`,
    "SameSite=Lax",
  ];
  if (typeof window !== "undefined" && window.location?.protocol === "https:") {
    segments.push("Secure");
  }
  const domain = cookieDomain();
  if (domain) segments.push(`Domain=${domain}`);
  document.cookie = segments.join("; ");
}

function deleteCookie(): void {
  if (typeof document === "undefined") return;
  const segments = [
    `${COOKIE_NAME}=`,
    "Path=/",
    "Max-Age=0",
    "SameSite=Lax",
  ];
  const domain = cookieDomain();
  if (domain) segments.push(`Domain=${domain}`);
  document.cookie = segments.join("; ");
}

export function saveDraft(seed: QrGeneratorSeed, options: SaveOptions = {}): void {
  if (typeof document === "undefined") return;
  const now = options.now ? options.now() : Date.now();
  const ttl = options.ttlMs ?? DEFAULT_TTL_MS;
  const envelope: DraftEnvelope = {
    v: SCHEMA_VERSION,
    expiresAt: now + ttl,
    seed,
  };
  try {
    writeCookie(JSON.stringify(envelope), ttl);
  } catch {
    // Cookie write can fail in obscure environments (disabled cookies,
    // sandboxed iframes). Losing the draft only degrades the seamless-
    // redirect experience — nothing actionable here.
  }
}

export function loadDraft(options: LoadOptions = {}): QrGeneratorSeed | null {
  if (typeof document === "undefined") return null;
  const raw = readRawCookie();
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    deleteCookie();
    return null;
  }

  if (!isEnvelope(parsed)) {
    deleteCookie();
    return null;
  }
  if (parsed.v !== SCHEMA_VERSION) {
    deleteCookie();
    return null;
  }
  const now = options.now ? options.now() : Date.now();
  if (parsed.expiresAt <= now) {
    deleteCookie();
    return null;
  }
  if (!isPayloadType(parsed.seed.type)) {
    deleteCookie();
    return null;
  }
  return parsed.seed;
}

export function clearDraft(): void {
  if (typeof document === "undefined") return;
  try {
    deleteCookie();
  } catch {
    // Best-effort — nothing actionable to do here.
  }
}

export function hasDraft(options: LoadOptions = {}): boolean {
  return loadDraft(options) !== null;
}

/**
 * Atomically reads and removes the current draft. Used by `/codes/new`
 * so the seamless redirect from the marketing generator only consumes
 * the draft once — refreshing the page or navigating elsewhere does not
 * re-hydrate stale state.
 */
export function consumeDraft(options: LoadOptions = {}): QrGeneratorSeed | null {
  const draft = loadDraft(options);
  if (draft) clearDraft();
  return draft;
}

function isEnvelope(value: unknown): value is DraftEnvelope {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.v === "number"
    && typeof obj.expiresAt === "number"
    && obj.seed !== null
    && typeof obj.seed === "object"
  );
}

export const DRAFT_COOKIE_NAME = COOKIE_NAME;
export const DRAFT_SCHEMA_VERSION = SCHEMA_VERSION;
